import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';
import SeoHead from '@/components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.diariode1trader.com';

type ContentBlock =
  | { type: 'intro'; text: string }
  | { type: 'text'; text: string }
  | { type: 'heading'; text: string }
  | { type: 'image'; url: string; caption: string }
  | { type: 'highlight'; title: string; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'quote'; text: string; author: string }
  | { type: 'cta'; title: string; text: string; buttonText: string; buttonUrl: string; coupon: string };

function renderBlock(block: ContentBlock, index: number) {
  switch (block.type) {
    case 'intro':
      return (
        <p key={index} className="text-lg text-slate-700 leading-relaxed font-medium border-l-4 border-blue-500 pl-5 mb-8">
          {block.text}
        </p>
      );
    case 'text':
      return (
        <p key={index} className="text-slate-600 leading-relaxed mb-6 text-base">
          {block.text}
        </p>
      );
    case 'heading':
      return (
        <h2 key={index} className="text-xl md:text-2xl font-extrabold text-[#0d1f3c] mt-10 mb-4">
          {block.text}
        </h2>
      );
    case 'image':
      return (
        <figure key={index} className="my-8">
          <div className="w-full h-64 md:h-96 rounded-2xl overflow-hidden">
            <img src={block.url} alt={block.caption} className="w-full h-full object-cover object-top" />
          </div>
          <figcaption className="text-center text-sm text-slate-400 mt-3 italic">{block.caption}</figcaption>
        </figure>
      );
    case 'highlight':
      return (
        <div key={index} className="bg-blue-50 border border-blue-100 rounded-2xl p-5 mb-6">
          <h4 className="font-bold text-blue-800 text-sm mb-2">{block.title}</h4>
          <p className="text-slate-600 text-sm leading-relaxed">{block.text}</p>
        </div>
      );
    case 'list':
      return (
        <ul key={index} className="space-y-2.5 mb-6">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
              <span className="mt-1 w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-checkbox-circle-fill text-blue-500 text-base"></i>
              </span>
              {item}
            </li>
          ))}
        </ul>
      );
    case 'quote':
      return (
        <blockquote key={index} className="my-8 bg-[#0d1f3c] rounded-2xl p-6 md:p-8">
          <i className="ri-double-quotes-l text-blue-400 text-3xl mb-3 block"></i>
          <p className="text-white text-base md:text-lg leading-relaxed font-medium mb-4">{block.text}</p>
          <cite className="text-blue-300 text-sm not-italic">— {block.author}</cite>
        </blockquote>
      );
    case 'cta':
      return (
        <div key={index} className="my-10 rounded-2xl overflow-hidden border border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-3 flex items-center gap-2">
            <i className="ri-star-fill text-yellow-300 text-sm"></i>
            <span className="text-white text-xs font-bold uppercase tracking-wider">Parceiro Recomendado</span>
          </div>
          <div className="p-6 md:p-8">
            <h3 className="text-xl md:text-2xl font-extrabold text-[#0d1f3c] mb-3">{block.title}</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-5">{block.text}</p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a
                href={block.buttonUrl}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold px-6 py-3 rounded-full transition-all whitespace-nowrap cursor-pointer"
              >
                {block.buttonText}
                <i className="ri-arrow-right-line"></i>
              </a>
              <div className="flex items-center gap-2 bg-white border border-emerald-200 rounded-full px-4 py-2">
                <i className="ri-price-tag-3-line text-emerald-600 text-sm"></i>
                <span className="text-xs text-slate-500">Cupão:</span>
                <span className="text-sm font-extrabold text-emerald-700 tracking-wider">{block.coupon}</span>
              </div>
            </div>
          </div>
        </div>
      );
    default:
      return null;
  }
}

export default function ArticleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const articleSchema = article
    ? [
        {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          dateModified: article.date,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Diário de 1 Trader',
            url: siteUrl,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/artigos/${article.id}`,
          },
          url: `${siteUrl}/artigos/${article.id}`,
          inLanguage: 'pt-PT',
        },
        {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Início',
              item: `${siteUrl}/`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Artigos',
              item: `${siteUrl}/artigos`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: article.title,
              item: `${siteUrl}/artigos/${article.id}`,
            },
          ],
        },
      ]
    : undefined;

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex flex-col items-center justify-center pt-28 pb-20">
          <i className="ri-article-line text-6xl text-slate-200 mb-4"></i>
          <h1 className="text-2xl font-bold text-[#0d1f3c] mb-2">Artigo não encontrado</h1>
          <p className="text-slate-400 mb-6">O artigo que procuras não existe ou foi removido.</p>
          <Link to="/artigos" className="bg-blue-600 text-white font-semibold px-6 py-3 rounded-full hover:bg-blue-700 transition-colors whitespace-nowrap">
            Ver todos os artigos
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const related = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={`${article.title} | Diário de 1 Trader`}
        description={article.excerpt}
        keywords={`${article.category}, trading, futuros, diário de 1 trader`}
        canonical={`/artigos/${article.id}`}
        ogImage={article.image}
        schema={articleSchema}
      />

      <Navbar />

      {/* Hero do artigo */}
      <div className="relative w-full h-64 md:h-[480px]">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1f3c]/90 via-[#0d1f3c]/50 to-transparent"></div>
        <div className="absolute inset-0 flex flex-col justify-end max-w-4xl mx-auto px-4 md:px-8 pb-10 md:pb-14 left-0 right-0">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/artigos" className="flex items-center gap-1.5 text-blue-300 hover:text-white text-sm transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Artigos
            </Link>
            <span className="text-slate-400 text-sm">/</span>
            <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
              <i className={`${article.categoryIcon} mr-1.5`}></i>
              {article.category}
            </span>
          </div>
          <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight max-w-3xl">{article.title}</h1>
        </div>
      </div>

      {/* Meta do artigo */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 border-b border-slate-100">
        <div className="flex flex-wrap items-center gap-4 md:gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <i className="ri-user-line text-blue-600 text-sm"></i>
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-700">{article.author}</div>
              <div className="text-xs text-slate-400">{article.authorRole}</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-slate-400 text-sm whitespace-nowrap">
            <i className="ri-calendar-line"></i>
            {article.date}
          </div>
          <div className="flex items-center gap-1.5 text-slate-400 text-sm whitespace-nowrap">
            <i className="ri-time-line"></i>
            {article.readTime} de leitura
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-14">
        {article.content.map((block, i) => renderBlock(block as ContentBlock, i))}
      </div>

      {/* Artigos relacionados */}
      {related.length > 0 && (
        <div className="bg-[#f0f6ff] py-14">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <h3 className="text-xl font-extrabold text-[#0d1f3c] mb-8">Artigos Relacionados</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((rel) => (
                <Link key={rel.id} to={`/artigos/${rel.id}`} className="group bg-white rounded-2xl overflow-hidden border border-blue-100 hover:border-blue-300 transition-all cursor-pointer">
                  <div className="h-36 overflow-hidden">
                    <img src={rel.image} alt={rel.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-4">
                    <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">{rel.category}</span>
                    <h4 className="font-bold text-[#0d1f3c] text-sm leading-snug mt-2 group-hover:text-blue-600 transition-colors line-clamp-2">{rel.title}</h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
